/**
 * Membership and RBAC types for the account-scoped membership system.
 *
 * IMPORTANT: Team members are STAFF ONLY (owner, admin, case_manager, supervisor).
 * Parents are NOT team members - they have separate access via the parent app.
 */

import { BaseEntity, DateField } from './base';

// ============================================================================
// MEMBER TYPES
// ============================================================================

/**
 * Member types for team members.
 * - staff: Office staff (owner, admin, case_manager) - invited via email
 * - supervisor: Field supervisors who conduct visits - invited via email or phone
 */
export type MemberType = 'staff' | 'supervisor';

/**
 * Sub-roles for staff members.
 * These map to the role IDs that staff members can be assigned.
 */
export type StaffRole = 'owner' | 'admin' | 'case_manager';

export type MemberStatus = 'invited' | 'active' | 'suspended';

export type InviteChannel = 'email' | 'sms';

// ============================================================================
// PERMISSIONS
// ============================================================================

/**
 * All available permissions in the system.
 * Permissions follow a resource.action pattern.
 */
export type Permission =
  // Team management
  | 'team.invite'
  | 'team.manage_roles'
  | 'team.read'
  // Case management
  | 'case.manage'
  | 'case.read.any'
  | 'case.read.assigned'
  // Visit management
  | 'visit.manage'
  | 'visit.read.any'
  | 'visit.read.case'
  | 'visit.confirm'
  // Billing
  | 'billing.manage'
  | 'billing.read'
  // Audit
  | 'audit.read'
  // Reports
  | 'report.manage'
  | 'report.read.any'
  | 'report.read.case'
  // Settings
  | 'settings.manage';

// ============================================================================
// DEFAULT ROLE DEFINITIONS
// ============================================================================

/**
 * Default role permissions for team members.
 *
 * Staff roles (for MemberType 'staff'):
 * - owner: Full account access, billing, settings
 * - admin: Team management, case management, settings
 * - case_manager: Case and visit management, reports
 *
 * Supervisor role (for MemberType 'supervisor'):
 * - supervisor: Visit execution, case-scoped access
 */
export const DEFAULT_ROLE_PERMISSIONS: Record<string, Permission[]> = {
  owner: [
    'team.invite',
    'team.manage_roles',
    'team.read',
    'case.manage',
    'case.read.any',
    'visit.manage',
    'visit.read.any',
    'billing.manage',
    'billing.read',
    'audit.read',
    'report.manage',
    'report.read.any',
    'settings.manage',
  ],
  admin: [
    'team.invite',
    'team.manage_roles',
    'team.read',
    'case.manage',
    'case.read.any',
    'visit.manage',
    'visit.read.any',
    'report.manage',
    'report.read.any',
    'settings.manage',
  ],
  case_manager: [
    'case.manage',
    'case.read.any',
    'visit.manage',
    'visit.read.any',
    'report.manage',
    'report.read.any',
    'team.read',
  ],
  supervisor: [
    'visit.read.case',
    'visit.manage',
    'case.read.assigned',
    'report.manage',
    'report.read.case',
  ],
};

// ============================================================================
// MEMBER DOCUMENT
// ============================================================================

/**
 * Member document stored at /accounts/{accountId}/members/{uid}
 */
export interface Member extends BaseEntity {
  memberType: MemberType;
  status: MemberStatus;
  roleIds: string[];
  /**
   * Materialized permissions array computed from roleIds.
   * This is maintained by Cloud Functions when roles change.
   */
  permissions: Permission[];
  invitedAt?: DateField;
  invitedByUid?: string;
}

// ============================================================================
// ROLE DOCUMENT
// ============================================================================

/**
 * Role document stored at /accounts/{accountId}/roles/{roleId}
 */
export interface Role extends BaseEntity {
  name: string;
  permissions: Permission[];
  isSystemRole?: boolean;
}

// ============================================================================
// INVITE DOCUMENT
// ============================================================================

/**
 * Invite document stored at /accounts/{accountId}/invites/{inviteId}
 */
export interface Invite extends BaseEntity {
  channel: InviteChannel;
  email?: string;
  phone?: string;
  intendedMemberType: MemberType;
  roleIds: string[];
  invitedByUid: string;
  expiresAt: DateField;
  acceptedAt?: DateField;
  acceptedUid?: string;
}

// ============================================================================
// INVITE INDEX DOCUMENT
// ============================================================================

/**
 * Invite index for quick lookup by email/phone.
 * Stored at /inviteIndex/{type}/{normalizedValue}/invites/{accountId_inviteId}
 */
export interface InviteIndexEntry {
  accountId: string;
  inviteId: string;
  createdAt: DateField;
}

// ============================================================================
// CASE LINK DOCUMENT
// ============================================================================

/**
 * Case link roles for team members (staff only).
 * Note: Parents don't have case links - they access cases through the parent app.
 */
export type CaseLinkRole = 'supervisor' | 'case_manager';

/**
 * Case link document stored at /accounts/{accountId}/members/{uid}/caseLinks/{caseId}
 * Links a team member to a specific case they work on.
 */
export interface CaseLink extends BaseEntity {
  role: CaseLinkRole;
}

// ============================================================================
// MIGRATION TRACKING
// ============================================================================

/**
 * Migration tracking document stored at /accounts/{accountId}/_migrations/{migrationId}
 */
export interface MigrationRecord {
  version: string;
  startedAt: DateField;
  completedAt?: DateField;
  stats: {
    membersCreated: number;
    invitesCreated: number;
    caseLinksCreated: number;
    rolesCreated: number;
    unresolvedIdentities: number;
  };
}

// ============================================================================
// CALLABLE FUNCTION INPUTS
// ============================================================================

export interface InviteMemberInput {
  accountId: string;
  channel: InviteChannel;
  email?: string;
  phone?: string;
  intendedMemberType: MemberType;
  roleIds: string[];
}

export interface SetMemberRolesInput {
  accountId: string;
  targetUid: string;
  roleIds: string[];
}

export interface SetMemberStatusInput {
  accountId: string;
  targetUid: string;
  status: MemberStatus;
}

export interface DeleteInviteInput {
  accountId: string;
  inviteId: string;
}
